// Alternate app icon sync (iOS + Android).
//
// The launcher icon follows the trainer's EXPLICIT appearance choices in
// Settings, resolved by one rule (in priority order):
//   1) Pro team accent set (instinct / valor / mystic) → that faction's icon,
//      whatever the theme. The accent is the louder identity statement.
//   2) everything else → the original dark icon.
//
// There is deliberately NO light-theme icon any more: the light-plate variant
// shipped in #763/#768 and was retired the same week (Sam, 2026-08-27 — the
// original dark chrome icon reads better on every wallpaper). themeMode stays
// a parameter so the resolver's signature (and its callers) didn't churn.
//
// Auto mode deliberately does NOT track the system light/dark flips: starting
// on iOS 26 every icon change shows a system alert (the plugin's
// suppressNotification flag is ignored), so flipping the icon at sunrise and
// sunset would nag the trainer twice a day. Only explicit selections made in
// Settings move the icon.
//
// Boot reconciliation reads the current icon first and no-ops when it already
// matches, so a relaunch never shows the alert.
//
// Android: the launcher icon is an <activity-alias> per faction in
// AndroidManifest.xml, and the plugin flips them with
// PackageManager.setComponentEnabledSetting. Two Android-only contracts, both
// of which fail silently rather than loudly if broken:
//   1) every call MUST carry `disable`. The plugin's Android bridge rejects
//      change()/reset() outright without it ("Must provide an array of icon
//      names to disable"), and the alias left enabled would otherwise linger
//      as a second launcher entry.
//   2) alias names are resolved as <packageName>.<name>, so FACTION_ICONS
//      below must match the android:name values in the manifest exactly.
// Note isSupported() is hardcoded `true` on Android (it is documented as iOS
// only), so it is not the guard it looks like: it cannot be relied on to keep
// this module inert on any platform.
// Web/dev: no-op via the dynamic import failing.

import type { AccentTheme, ThemeMode } from "@/lib/accentPersist";
import { getPlatform } from "@/lib/platform";

/**
 * ANDROID IS OFF (2026-09-16, Discord: two Galaxy testers on Android 16, "after
 * changing the color in Appearance the app starts to crash; the only
 * workaround is to wipe app data"). The plugin's Android `change()` does not
 * only enable the faction alias: it DISABLES the `.MainActivity` component
 * itself, the very Activity every alias targets and every intent (OAuth
 * return, deep link, the launcher) resolves to. A disabled Activity cannot be
 * started, so the app "crashes" on every launch from then on, and clearing
 * data does not touch component state (a reinstall does). The in-app accent
 * still applies on Android; only the launcher icon stays the default. iOS
 * keeps the alternate icons (a supported, reversible OS feature there).
 * MainActivity.onCreate also heals any device that can still start.
 */
export const APP_ICON_SWITCH_PLATFORMS: ReadonlySet<string> = new Set(["ios"]);

// Appiconset names registered in Assets.xcassets +
// ASSETCATALOG_COMPILER_ALTERNATE_APPICON_NAMES (ios/App/App.xcodeproj).
const FACTION_ICONS: Record<Exclude<AccentTheme, "nexus">, string> = {
  instinct: "AppIconInstinct",
  valor: "AppIconValor",
  mystic: "AppIconMystic",
};

// Every alias the manifest declares. Android needs the full disable set on
// each call (see above); iOS ignores it.
const ALL_ICONS = Object.values(FACTION_ICONS);

export function resolveIconName(
  _themeMode: ThemeMode,
  accent: AccentTheme,
  hasPro: boolean,
): string | null {
  if (hasPro && accent !== "nexus") return FACTION_ICONS[accent];
  return null; // original icon
}

let lastApplied: string | null | undefined; // undefined = not yet read

/**
 * Reconcile the launcher icon with the resolved target. Safe to call on
 * every settings change and on boot; only actually switches (and therefore
 * only shows the iOS system alert) when the icon genuinely differs.
 */
export async function syncAppIcon(
  themeMode: ThemeMode,
  accent: AccentTheme,
  hasPro: boolean,
): Promise<void> {
  if (!APP_ICON_SWITCH_PLATFORMS.has(getPlatform())) return;
  try {
    const { AppIcon } = await import("@capacitor-community/app-icon");
    const supported = await AppIcon.isSupported();
    if (!supported?.value) return;

    const target = resolveIconName(themeMode, accent, hasPro);

    if (lastApplied === undefined) {
      const current = await AppIcon.getName();
      lastApplied = current?.value ?? null;
    }
    if (lastApplied === target) return;

    if (target === null) {
      await AppIcon.reset({ suppressNotification: true, disable: ALL_ICONS });
    } else {
      await AppIcon.change({
        name: target,
        suppressNotification: true,
        disable: ALL_ICONS.filter((n) => n !== target),
      });
    }
    lastApplied = target;
  } catch {
    // Not a native runtime, or the OS refused the switch (e.g. app in
    // background). The next explicit settings change retries naturally.
  }
}
